import { CrudApi } from './CrudApi';
import { CalendarModel } from '@/models/calendar/CalendarModel';
import { CalendarEventModel } from '@/models/calendar/CalendarEventModel';
import { getBrowserTimezone } from '@/helpers/date';
import { QueryOptions } from '@/../types/interfaces';
import { AxiosRequestConfig } from 'axios';
import { rootUrl } from "./api";
export class CalendarApi extends CrudApi<CalendarModel>{
	constructor(protected parentResource: string, protected parentId: string) {
		super(`${parentResource}/${parentId}/calendar`, (obj) => new CalendarModel(parentId).load(obj));
	}
	async findOrCreateCalendarByName(name?: string, options: QueryOptions = {}, config: AxiosRequestConfig = {}): Promise<CalendarModel> {
		try {
			const obj = await CrudApi.Api((c) => c.get(`/${this.resource}/findOrCreateCalendarByName`, this.applyOptionsToRequest(options, {
				...config,
				params:{
					name,
					timezone: getBrowserTimezone(),
					...config.params,
				}
			})));
			return this.create(obj);
		} catch (e) {
			if (e.response.status === 404) {
				return null;
			}
			throw e;
		}
	}

	generateICSURL(id: string): string {
		return rootUrl + `/calendar/ics/${id}`;
	}
}

export class CalendarEventsApi extends CrudApi<CalendarEventModel>{
	constructor(protected parentResource: string, protected parentId: string, protected calendarId: string) {
		super(`${parentResource}/${parentId}/calendar/${calendarId}/calendarEvent`, (obj) => new CalendarEventModel(calendarId).load(obj));
	}
	
	async deleteByRootId(rootId: string, config?: AxiosRequestConfig): Promise<any>{
		const res = await CrudApi.Api((c) => c.delete(`/${this.resource}/delete-by-root-id/${rootId}`, config));
		return res;
	}

	async pruneByRootId(rootId: string, after: Date, config?: AxiosRequestConfig): Promise<any> {
		const res = await CrudApi.Api((c) => c.post(`/${this.resource}/prune/${rootId}`, { after }, config));
		return res;
	}

	async setRecurrenceUntilByRootId(rootId:string, until: Date, config?: AxiosRequestConfig): Promise<any> {
		const res = await CrudApi.Api((c) => c.post(`/${this.resource}/set-until-by-root-id/${rootId}`, { until }, config));
		return res;
	}

	/**
	 * Set the current user's own availability response (+ optional note).
	 * occurrenceDate (YYYY-MM-DD) answers for one date of a repeating event instead of the series.
	 */
	async setRsvp(eventId: string, rsvp: string, note?: string, occurrenceDate?: string): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/rsvp`, { rsvp, note, occurrenceDate }));
		return this.create(obj);
	}
	/** Coach sets another participant's availability on their behalf, for one date or the series. */
	async setParticipantRsvp(eventId: string, participantId: string, rsvp: string, note?: string, occurrenceDate?: string): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/participant/${participantId}/rsvp`, { rsvp, note, occurrenceDate }));
		return this.create(obj);
	}
	/** Staff records one participant's attendance (on-time / late / absent). */
	async setParticipantAttendance(eventId: string, participantId: string, tardiness: string): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/participant/${participantId}/attendance`, { tardiness }));
		return this.create(obj);
	}
	/** Staff records attendance for several participants at once. */
	async saveAttendance(eventId: string, entries: { participantId: string; tardiness: string }[]): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/attendance`, { entries }));
		return this.create(obj);
	}
	/** Staff opens (false) or closes (true) attendance taking for this event. */
	async setAttendanceFinished(eventId: string, finished: boolean): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/attendance/finish`, { finished }));
		return this.create(obj);
	}
	/** Coach adds a task/assignment. */
	async addAssignment(eventId: string, params: { title: string; description?: string; assigneeIds?: string[] }): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/assignment`, params));
		return this.create(obj);
	}
	/** Coach removes a task/assignment. */
	async deleteAssignment(eventId: string, assignmentId: string): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.delete(`/${this.resource}/${eventId}/assignment/${assignmentId}`));
		return this.create(obj);
	}
	/** Current user toggles their own completion of an assignment. */
	async toggleAssignment(eventId: string, assignmentId: string): Promise<CalendarEventModel> {
		const obj = await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/assignment/${assignmentId}/toggle`, {}));
		return this.create(obj);
	}
	/** Coach: remind participants who haven't responded, for one date or the whole series. */
	async remindNonResponders(eventId: string, occurrenceDate?: string): Promise<{ reminded: number }> {
		return await CrudApi.Api((c) => c.post(`/${this.resource}/${eventId}/remind-non-responders`, { occurrenceDate }));
	}
}